import { useEffect, useState } from "react";

import type { PersonId, RecordingId, SessionId } from "@don/wire";

/**
 * The Console's routes: Sessions, a session, Knowledge, Settings and sign-in (issue 192).
 *
 * No router library: architecture §3 asks for "no router library beyond what two views
 * need", and what two views need is a `pathname` parsed one way and a `pushState` wrapped
 * once. Real paths rather than a hash, because `/sessions/{id}` is what a pasted link, a
 * bookmark and this issue's acceptance criterion all spell.
 */
export type Route =
  | { readonly kind: "sessions" }
  /**
   * A session's detail, or with `recording` a recording's (`/recordings/{id}`, issue 321):
   * one screen, read from `GET /recordings/{id}`, and `sessionId` holds the recording's id.
   */
  | { readonly kind: "session"; readonly sessionId: SessionId; readonly recording?: true }
  | { readonly kind: "knowledge"; readonly personId: PersonId | null }
  | { readonly kind: "settings" }
  | { readonly kind: "sign-in" }
  /**
   * The Connections page (issue 392): the employee's page, mounted by `main.tsx` in place of
   * the owner Console, never inside it.
   */
  | { readonly kind: "connect" }
  /**
   * Where Microsoft's admin consent lands the IT admin (issue 466, Outlook design-spec §2.2, W5):
   * public, mounted by `main.tsx` outside the owner shell, reading only the api's 303 query.
   */
  | { readonly kind: "admin-callback" }
  | { readonly kind: "not-found"; readonly path: string };

/** The library (issue 192). `/` and {@link RECORDINGS} resolve here. */
export const SESSIONS = "/sessions";
/** The library's old path, kept as an alias so a saved link still lands (issue 192). */
export const RECORDINGS = "/recordings";
export const KNOWLEDGE = "/knowledge";
export const SETTINGS = "/settings";
/** Where a signed-out owner is sent, and the one page that reads nothing (issue 118). */
export const SIGN_IN = "/sign-in";
/** The employee's Connections page (issue 392), reached only from the pill's Connect link. */
export const CONNECT = "/connect";
/** The admin callback page (issue 466): the api's admin callback 303s here with `?r=`. */
export const ADMIN_CALLBACK = "/connect/microsoft/admin-callback";

/**
 * Where a row click goes. One function, so the link and the parser cannot disagree. `atMs`, a
 * place in the session (issue 280, design-spec §9.6), adds `?at=` when it is a non-negative
 * integer and nothing otherwise; {@link atMsOf} reads it back.
 */
export function sessionPath(sessionId: SessionId, atMs?: number): string {
  const path = `/sessions/${encodeURIComponent(sessionId)}`;
  return atMs !== undefined && Number.isSafeInteger(atMs) && atMs >= 0 ? `${path}?at=${atMs}` : path;
}

/** Where a recording's row goes (issue 321): `/recordings/{id}`, `atMs` as {@link sessionPath}'s. */
export function recordingPath(recordingId: RecordingId, atMs?: number): string {
  const path = `${RECORDINGS}/${encodeURIComponent(recordingId)}`;
  return atMs !== undefined && Number.isSafeInteger(atMs) && atMs >= 0 ? `${path}?at=${atMs}` : path;
}

/**
 * Where a moment in a recording's segment opens (issue 322, PRD stories 26 and 27): the
 * recording at the segment's start on the recording's timeline plus `atMs` into the segment.
 * No `atMs` (a bookend) opens at the segment's start; a segment the recording does not list opens
 * the recording at its start. `segments` are the recording read's (`startMs` is the route's
 * offset, never recomputed here).
 */
export function segmentMomentPath(
  recordingId: RecordingId,
  segments: readonly { readonly sessionId: SessionId; readonly startMs: number }[],
  sessionId: SessionId,
  atMs?: number,
): string {
  const segment = segments.find((each) => each.sessionId === sessionId);
  return segment === undefined ? recordingPath(recordingId) : recordingPath(recordingId, segment.startMs + (atMs ?? 0));
}

/** Where a library row opens: its recording, or its session (issue 321). */
export function rowPath(row: { readonly sessionId: SessionId; readonly recordingId?: RecordingId }): string {
  return row.recordingId === undefined ? sessionPath(row.sessionId) : recordingPath(row.recordingId);
}

/**
 * The `at` of a session URL's query (issue 280): milliseconds into the session, accepted only
 * when it is all digits, so `?at=abc` and `?at=-5` open the session at its start.
 */
export function atMsOf(search: string): number | undefined {
  const at = new URLSearchParams(search).get("at");
  if (at === null || !/^\d+$/.test(at)) return undefined;
  const ms = Number(at);
  return Number.isSafeInteger(ms) ? ms : undefined;
}

/**
 * A person on the Knowledge page (issue 276, design-spec §9.7). One function, so the link and
 * the parser cannot disagree.
 */
export function knowledgePath(personId: PersonId): string {
  return `${KNOWLEDGE}/${encodeURIComponent(personId)}`;
}

/**
 * Pure, because it is the half worth asserting without a browser.
 *
 * `/`, `/sessions` and `/recordings` are the library: the Console's entry point is the
 * library, so the root is not a landing page. Anything unrecognized is not found.
 */
export function parseRoute(pathname: string): Route {
  try {
    return routeOf(pathname);
  } catch {
    // A malformed `%` escape makes `decodeURIComponent` throw: that path names nothing.
    return { kind: "not-found", path: pathname };
  }
}

function routeOf(pathname: string): Route {
  const segments = pathname.split("/").filter((segment) => segment.length > 0);
  if (segments.length === 0) return { kind: "sessions" };
  if (segments.length === 1 && (segments[0] === "sessions" || segments[0] === "recordings")) {
    return { kind: "sessions" };
  }
  if (segments.length === 1 && segments[0] === "knowledge") return { kind: "knowledge", personId: null };
  if (segments.length === 2 && segments[0] === "knowledge" && segments[1]) {
    return { kind: "knowledge", personId: decodeURIComponent(segments[1]) };
  }
  if (segments.length === 1 && segments[0] === "settings") return { kind: "settings" };
  if (segments.length === 1 && segments[0] === "sign-in") return { kind: "sign-in" };
  if (segments.length === 1 && segments[0] === "connect") return { kind: "connect" };
  if (`/${segments.join("/")}` === ADMIN_CALLBACK) return { kind: "admin-callback" };
  if (segments.length === 2 && segments[0] === "sessions" && segments[1]) {
    return { kind: "session", sessionId: decodeURIComponent(segments[1]) };
  }
  if (segments.length === 2 && segments[0] === "recordings" && segments[1]) {
    return { kind: "session", sessionId: decodeURIComponent(segments[1]), recording: true };
  }
  return { kind: "not-found", path: pathname };
}

/**
 * The URL an alias is replaced with, or `null` when the path is already canonical. Only `/`
 * and {@link RECORDINGS} are aliases; the query string is kept.
 */
export function canonicalPath(pathname: string, search: string): string | null {
  return pathname === "/" || pathname === RECORDINGS ? `${SESSIONS}${search}` : null;
}

/**
 * Push a route and tell the app.
 *
 * `pushState` does not fire `popstate` — that is the API, not an oversight — so the one
 * place that pushes is also the one place that announces, and a caller cannot navigate
 * without the screen following.
 */
export function navigate(path: string): void {
  window.history.pushState(null, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

/**
 * {@link navigate} without a history entry: a guarded page that redirected to sign-in is not
 * somewhere the back button should return to, because it would redirect again.
 */
export function redirect(path: string): void {
  window.history.replaceState(null, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

/**
 * The current route, following the back button as well as `navigate`. An alias is replaced
 * in place by its canonical URL ({@link canonicalPath}), with no history entry, so the
 * address bar reads `/sessions` after `/recordings` loads.
 */
export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(currentRoute);
  useEffect(() => {
    const follow = () => setRoute(currentRoute());
    window.addEventListener("popstate", follow);
    return () => window.removeEventListener("popstate", follow);
  }, []);
  return route;
}

function currentRoute(): Route {
  const { pathname, search, hash } = window.location;
  const canonical = canonicalPath(pathname, search);
  if (canonical === null) return parseRoute(pathname);
  window.history.replaceState(null, "", `${canonical}${hash}`);
  return parseRoute(SESSIONS);
}
